import { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Loader2, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformOnboarding = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/onboarding`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch (err) {
        toast.error('Failed to load onboarding data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // load once on mount

  return (
    <div className="space-y-4" data-testid="platform-onboarding">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Onboarding Intelligence</h1>
        <p className="text-xs text-slate-500">Market intelligence from company onboarding surveys</p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
        ) : data.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mx-auto">
              <FileText className="w-5 h-5 text-slate-500" />
            </div>
            <p className="text-xs text-slate-500">No onboarding data yet</p>
            <p className="text-[10px] text-slate-600">Data will appear here once companies complete their onboarding questionnaire</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-[10px] text-slate-500 font-medium">Company</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">CRM Used</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Projects</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Pain Points</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Sales Setup</TableHead>
                <TableHead className="text-[10px] text-slate-500 font-medium">Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d, i) => (
                <TableRow key={d.company_id || i} className="border-slate-800/50 hover:bg-slate-800/30">
                  <TableCell>
                    <p className="text-xs font-medium text-slate-200">{d.company_name}</p>
                    <p className="text-[10px] text-slate-500">{d.billing_email}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400">
                      {d.crm_used || 'None'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">{d.number_of_projects || '-'}</TableCell>
                  <TableCell className="max-w-[200px]">
                    <p className="text-[10px] text-slate-400 truncate" title={d.pain_points}>
                      {d.pain_points || '-'}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-[150px]">
                    <p className="text-[10px] text-slate-400 truncate" title={d.sales_setup}>
                      {d.sales_setup || '-'}
                    </p>
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-500">
                    {d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Summary Stats */}
      {data.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs font-medium text-slate-300 mb-2">Top CRMs</p>
            <div className="space-y-1">
              {Object.entries(
                data.reduce((acc, d) => {
                  const crm = d.crm_used || 'None';
                  acc[crm] = (acc[crm] || 0) + 1;
                  return acc;
                }, {})
              ).sort((a, b) => b[1] - a[1]).map(([crm, count]) => (
                <div key={crm} className="flex justify-between text-[10px]">
                  <span className="text-slate-400">{crm}</span>
                  <span className="text-slate-500">{count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs font-medium text-slate-300 mb-2">Common Pain Points</p>
            <div className="space-y-1">
              {data.filter(d => d.pain_points).slice(0, 5).map((d) => (
                <p key={d.company_id} className="text-[10px] text-slate-400 truncate" title={d.pain_points}>
                  {d.company_name}: {d.pain_points}
                </p>
              ))}
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-xs font-medium text-slate-300 mb-2">Avg Projects per Company</p>
            <p className="text-2xl font-bold text-slate-100">
              {data.length > 0 ? (data.reduce((sum, d) => sum + (parseInt(d.number_of_projects) || 0), 0) / data.length).toFixed(1) : 0}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformOnboarding;
